<template>
	<div>
		<template v-if="hasAccessToFamb">
			<LoadingIcon v-if="isLoading" class="aligned-center" />

			<FallBack
				v-if="
					errorType === FALLBACK_ERROR_TYPES.SAVE_SETTINGS_ERROR ||
					errorType === FALLBACK_ERROR_TYPES.GET_SETTINGS_ERROR
				"
				iconStyle="far"
				icon="bell"
				:message="error"
			/>
			<div v-if="canModifySettings">
				<section
					class="b360-grid-row preference-center-section"
					data-testid="preference-center-section"
				>
					<TitleDescription
						class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 b360-col-lg-4 form-title-description"
						:title="'General'"
						:description="'Decide what features you want your profile listing to include.'"
					></TitleDescription>
					<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-8 b36-col-lg-8">
						<div class="url-link-wrapper" data-test="companyProfileURL">
							<div class="label-wrapper">
								<div class="b360-form-label company-url-label">
									Company Profile URL
									<button
										class="clipboard-button"
										@click.prevent="copyText(fambCompanyProfileURL)"
										title="Copy to Clipboard"
										aria-label="Copy Your Company Profile URL to Clipboard"
									>
										<font-awesome-icon
											class="edit-icon"
											:icon="['far', 'clipboard']"
										/>
									</button>
								</div>
							</div>
							<a
								:href="fambCompanyProfileURL"
								target="_blank"
								class="company-profile-url"
								>{{ fambCompanyProfileURL }}</a
							>
						</div>
						<transition name="fade-url">
							<div
								class="url-link-wrapper"
								v-if="settings.enableProfileInd"
								data-test="yourProfileURL"
							>
								<div class="label-wrapper edit-label-wrapper">
									<div class="b360-form-label">Your Profile URL</div>
									<button
										id="editYourProfileURL"
										class="edit-button"
										@click="toggleEditInput($event, fambYourProfileURL)"
										aria-label="Edit Your Profile URL"
										:aria-expanded="!editProfileURLHidden"
										:disabled="isSaving"
										data-testid="editYourProfileURL"
									>
										<font-awesome-icon
											class="edit-icon"
											:class="{ selected: !editProfileURLHidden }"
											:icon="['far', 'pen']"
										/>
									</button>
									<button
										class="clipboard-button"
										@click.prevent="copyText(fambYourProfileURL)"
										title="Copy to Clipboard"
										aria-label="Copy Your Profile URL to Clipboard"
									>
										<font-awesome-icon
											class="edit-icon"
											:icon="['far', 'clipboard']"
										/>
									</button>
								</div>
								<div class="edit-warning">
									Changing your URL will disable your previous link. Be sure to
									update it anywhere it is currently used.
								</div>
								<a
									:href="fambYourProfileURL"
									target="_blank"
									v-if="editProfileURLHidden"
								>
									{{ fambYourProfileURL }}
								</a>
								<div v-else>
									<div
										class="edit-url-wrapper"
										aria-labelledby="editYourProfileURL"
									>
										<span>
											{{ profilePath }}
										</span>
										<BaseFormInput
											ref="profileSlug"
											type="text"
											name="profileSlug"
											v-model="$v.profileSlug.$model"
											:error="$v.profileSlug.$error"
											:icon="['far', 'times']"
											iconLabel="Clear Input Value"
											:inputMask="SPECIAL_CHARACTER_MASK"
											:value="profileSlug"
											@onIconClick="clearProfileEditURL"
											@submit.prevent
											@input="handleProfileSlugInput"
										/>
									</div>
									<div
										class="error"
										v-if="
											!$v.profileSlug.minLength || !$v.profileSlug.maxLength
										"
									>
										You may enter between 3-100 letters or numbers.
									</div>
									<div class="error" v-if="!$v.profileSlug.urlIsNotUnique">
										{{ error }}
									</div>
									<div
										class="error"
										v-if="!$v.profileSlug.required"
										data-testid="empty-url-error"
									>
										URL cannot be empty.
									</div>
								</div>
							</div>
						</transition>

						<div class="toggle-switch-wrapper">
							<div class="label-wrapper">
								<div id="enableProfileInd" class="b360-form-label">
									Enable Listing
								</div>
								<span class="form-label-subheading">
									Display your profile listing for borrowers and real estate
									agents to find you.
								</span>
							</div>
							<ToggleSwitch
								@click="enableProfileClickHandler"
								:value="settings.enableProfileInd"
								name="enableProfileInd"
								:disabled="mainProfileIsInternalUser"
								aria-labelledby="enableProfileInd"
							></ToggleSwitch>
						</div>
						<div class="toggle-switch-wrapper">
							<div class="label-wrapper">
								<div id="contactProfileInd" class="b360-form-label">
									Contact Form
								</div>
								<span class="form-label-subheading">
									Include a contact form on your profile for borrowers to email
									you.
								</span>
							</div>
							<ToggleSwitch
								@click="contactProfileClickHandler"
								:value="settings.contactProfileInd"
								name="contactProfileInd"
								:disabled="mainProfileIsInternalUser"
								aria-labelledby="contactProfileInd"
							></ToggleSwitch>
						</div>
						<div class="toggle-switch-wrapper">
							<div class="label-wrapper">
								<div
									id="BlinkIntegrationInd"
									class="b360-form-label"
									:class="{
										'b360-form-label__disabled': !hasBlink,
									}"
								>
									Blink+ Integration
								</div>
								<span
									class="form-label-subheading"
									:class="{
										'form-label-subheading__disabled': !hasBlink,
									}"
								>
									Include your Blink+ link in your profile to allow borrowers to
									submit an application.
								</span>
							</div>
							<ToggleSwitch
								v-if="showBlink"
								@click="blinkIntegrationClickHandler"
								:value="settings.blinkIntegrationInd"
								name="blinkIntegrationInd"
								:disabled="mainProfileIsInternalUser"
								aria-labelledby="blinkIntegrationInd"
							></ToggleSwitch>
							<ToggleSwitch
								v-else
								:value="settings.blinkIntegrationInd"
								name="blinkIntegrationInd"
								:disabled="true"
								aria-labelledby="blinkIntegrationInd"
							></ToggleSwitch>
						</div>
					</div>
				</section>

				<section
					class="b360-grid-row preference-center-section"
					data-testid="preference-center-section"
				>
					<TitleDescription
						class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 b360-col-lg-4 form-title-description"
						:title="'Specializations'"
						:description="'Include your specializations on your profile listing.'"
					></TitleDescription>
					<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-8 b36-col-lg-8">
						<template v-for="spec in specializations">
							<fieldset
								:key="spec.legend"
								:data-test="spec.legend"
								:class="spec.legend === 'Languages' ? 'languages' : ''"
							>
								<legend>
									{{ spec.legend }}
									<div class="sub-legend">
										{{ spec.subLegend }}
									</div>
								</legend>
								<div
									class="checkbox-list"
									v-for="(item, index) in spec.data"
									:key="index"
								>
									<FormCheckbox
										v-model="item.activeInd"
										:disabled="mainProfileIsInternalUser"
										>{{ item.specializationName }}</FormCheckbox
									>
								</div>
							</fieldset>
						</template>
					</div>
				</section>

				<SaveButton
					:waiting="isSaving"
					:saved="recentlySaved"
					@handle-click="saveButtonHandler()"
					:disabled="mainProfileIsInternalUser || $v.$error"
				></SaveButton>
			</div>
		</template>
		<AccessDenied v-else :item="'MortgageMatchup.com'" />
	</div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { cloneDeep, debounce, isEqual } from 'lodash';
import { minLength, maxLength, required } from 'vuelidate/lib/validators';
import { splitProfileURL } from './helpers';
import { SPECIAL_CHARACTER_MASK, FALLBACK_ERROR_TYPES } from './constants';

import AccessDenied from '../../components/global/AccessDenied.vue';
// it does not look like CheckBox component is used here
import CheckBox from '@/components/global/CheckBox.vue';
import FallBack from '../../components/global/FallBack.vue';
import FormCheckbox from '@/components/global/FormCheckbox.vue';
import BaseFormInput from '@/components/global/BaseFormInput.vue';
import LoadingIcon from '../../components/global/LoadingIcon';
import SaveButton from '../../components/preference-center/SaveButton.vue';
import TitleDescription from '../../components/global/TitleDescription.vue';
import ToggleSwitch from '../../components/global/ToggleSwitch.vue';

export default {
	name: 'PreferenceCenterFindAMortgageBroker',
	components: {
		AccessDenied,
		CheckBox,
		FallBack,
		FormCheckbox,
		BaseFormInput,
		LoadingIcon,
		SaveButton,
		TitleDescription,
		ToggleSwitch,
	},
	data() {
		return {
			settings: {
				enableProfileInd: null,
				contactProfileInd: null,
				blinkIntegrationInd: null,
				fambSpecialization: [],
			},
			specializations: [],
			recentlySaved: false,
			editProfileURLHidden: true,
			profileSlug: '',
			profilePath: '',
		};
	},
	computed: {
		...mapState({
			currentSettings: (state) => state.preferenceCenterFamb.settings,
			isSaving: (state) => state.preferenceCenterFamb.isSaving,
			isLoading: (state) => state.preferenceCenterFamb.isLoading,
			error: (state) => state.preferenceCenterFamb.error,
			errorType: (state) => state.preferenceCenterFamb.errorType,
			contactId: (state) => state.profile.contactId,
			fambYourProfileURL: (state) => state.profile.fambYourProfileURL,
			fambCompanyProfileURL: (state) =>
				state.companyProfile.fambCompanyProfileURL,
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
			blinkLink: (state) => state.profile.blinkLink,
		}),
		...mapGetters(['hasAccessToFamb', 'hasBlink']),
		canModifySettings() {
			return (
				!this.isLoading &&
				(this.errorType === null ||
					this.errorType === 'URL_NOT_UNIQUE_ERROR') &&
				this.currentSettings.enableProfileInd !== null
			);
		},
		showBlink() {
			return this.hasBlink && this.blinkLink !== '' && this.blinkLink !== null;
		},
	},
	methods: {
		...mapActions([
			'cancelProfileURLValidityCheck',
			'checkProfileURLValidity',
			'clearInputErrors',
			'getFambSettings',
			'resetFambState',
			'saveFambSettings',
		]),
		handleProfileSlugInput: debounce(async function () {
			this.checkProfileURLValidity(this.profileSlug);
		}, 500),
		enableProfileClickHandler() {
			this.settings.enableProfileInd = !this.settings.enableProfileInd;
			if (!this.settings.enableProfileInd && !this.editProfileURLHidden) {
				this.editProfileURLHidden = true;
				this.setProfileURLParts(this.fambYourProfileURL);
			}
		},
		contactProfileClickHandler() {
			this.settings.contactProfileInd = !this.settings.contactProfileInd;
		},
		blinkIntegrationClickHandler() {
			this.settings.blinkIntegrationInd = !this.settings.blinkIntegrationInd;
		},
		async saveButtonHandler() {
			await this.saveFambSettings({
				...this.settings,
				blinkIntegrationInd: this.hasBlink
					? this.settings.blinkIntegrationInd
					: false,
				fambYourProfileURLPath: this.profilePath,
				fambYourProfileURLSlug: this.profileSlug,
			});
			this.showInvalidItem();
		},
		copyCurrentSettings() {
			this.settings = cloneDeep(this.currentSettings);
			// UI loops through the specializations to display the Specializations section.
			this.specializations = [
				{
					legend: 'Loan Services',
					subLegend: 'Include loan services you offer.',
					data: this.settings.fambSpecialization.filter(
						(item) => item.specializationType === 'LoanServices',
					),
				},
				{
					legend: 'Loan Products',
					subLegend: 'Include the loan products you offer.',
					data: this.settings.fambSpecialization.filter(
						(item) => item.specializationType === 'LoanProducts',
					),
				},
				{
					legend: 'Languages',
					subLegend: 'Include the languages you speak.',
					data: this.settings.fambSpecialization
						.filter((item) => item.specializationType === 'Languages')
						.sort((a, b) =>
							a.specializationName.localeCompare(b.specializationName),
						),
				},
			];
		},
		getFambDataForUser() {
			// If user has access but doesn't already have settings, go get them
			if (
				this.hasAccessToFamb &&
				this.currentSettings.enableProfileInd === null
			) {
				this.getFambSettings();
			}
		},
		setProfileURLParts(profileURL) {
			let { urlSlug, urlPath } = splitProfileURL(profileURL);
			this.profilePath = urlPath;
			this.profileSlug = urlSlug;
			if (this.errorType === 'URL_NOT_UNIQUE_ERROR') {
				this.clearInputErrors();
			}
		},
		toggleEditInput(event, currentProfileURL) {
			event.preventDefault();
			if (!this.editProfileURLHidden) {
				this.setProfileURLParts(this.fambYourProfileURL);
			}
			this.editProfileURLHidden = !this.editProfileURLHidden;
		},
		clearProfileEditURL() {
			this.profileSlug = '';
		},
		handleFambYourProfileURLChange(newProfileURL) {
			this.setProfileURLParts(newProfileURL);
			if (!this.editProfileURLHidden) {
				this.editProfileURLHidden = true;
			}
		},
		showInvalidItem() {
			/*
			 * Currently, profileSlug (part of the Your Profile URL) is the only field to show
			 * validation errors for. These errors should be ignored if the field isn't shown.
			 */
			if (this.$refs.profileSlug) {
				if (
					this.$v.profileSlug.$invalid ||
					this.errorType === 'URL_NOT_UNIQUE_ERROR'
				) {
					this.$refs.profileSlug.$el.scrollIntoView(false);
				}
			}
		},
		copyText(text) {
			navigator.clipboard.writeText(text);
		},
	},
	watch: {
		contactId() {
			// The contactId changes when impersonating. Clear old data and get new.
			this.resetFambState();
			this.getFambDataForUser();
			this.cancelProfileURLValidityCheck();
		},
		currentSettings: {
			handler: 'copyCurrentSettings',
			immediate: true,
		},
		fambYourProfileURL: {
			handler: 'handleFambYourProfileURLChange',
			immediate: true,
		},
		isSaving(currentIsSaving, previousIsSaving) {
			/*
			 * The value of recentlySaved controls if the save button is in saved state.
			 * If there are no errors and we currently aren't saving but we previously were,
			 * we must have successfully saved. Show the saved state for a short time.
			 */
			const profileSlugError =
				this.$v.profileSlug && this.$v.profileSlug.$error;

			if (!this.error && currentIsSaving && !profileSlugError) {
				this.recentlySaved = true;
				setTimeout(() => {
					this.recentlySaved = false;
				}, 1000);
			}
		},
	},
	validations() {
		if (this.editProfileURLHidden) {
			return {};
		} else {
			return {
				profileSlug: {
					minLength: minLength(3),
					maxLength: maxLength(100),
					required,
					urlIsNotUnique: function () {
						return !this.errorType;
					},
				},
			};
		}
	},
	mounted() {
		this.getFambDataForUser();
	},
	created() {
		this.SPECIAL_CHARACTER_MASK = SPECIAL_CHARACTER_MASK;
		this.FALLBACK_ERROR_TYPES = FALLBACK_ERROR_TYPES;
	},
	beforeRouteLeave(to, from, next) {
		const fambYourProfileURLChanged =
			this.fambYourProfileURL !== this.profilePath + this.profileSlug;
		const settingsChanged = !isEqual(this.settings, this.currentSettings);
		if (fambYourProfileURLChanged || settingsChanged) {
			// TODO: Use a modal for better UX
			// eslint-disable-next-line no-alert
			const answer = window.confirm(
				'There are unsaved changes to your MortgageMatchup.com Settings.  Are you sure you want to continue?',
			);
			if (answer === true) {
				this.copyCurrentSettings();
			}
			next(answer);
			this.cancelProfileURLValidityCheck();
		} else {
			next();
		}
	},
};
</script>

<style scoped lang="scss">
.languages {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	width: fit-content;
}
.company-url-label {
	display: flex;
	justify-content: flex-start;
}
.title-description {
	grid-row-end: span 1;
}

.url-link-wrapper {
	@include rem(padding-bottom, 12px);
	max-height: 250px;

	a {
		font-weight: bold;
		overflow-wrap: break-word;
		word-wrap: break-word;
		white-space: normal;
		text-decoration: none;
		color: $brand-blue;
	}
}

.fade-url-leave-active,
.fade-url-enter-active {
	max-height: 250px;
	transition: opacity $b360-motion-duration--medium
			$b360-motion-easing--standard,
		max-height $b360-motion-duration--longer;
}

.fade-url-enter,
.fade-url-leave-active {
	max-height: 0;
	opacity: 0;
}

.edit-label-wrapper {
	align-items: center;
	display: flex;
}

.edit-warning {
	@include rem(padding, 3px 0);
	font-weight: normal;
	color: $text-base-color;
}

.edit-button,
.clipboard-button {
	background: transparent;
	border: none;
	@include rem(margin, -2px 0 0 2px);
	@include rem(padding, 4px 0);
	border: 1px solid transparent;
	&:hover {
		border: 1px solid $b360-color--blue;
		border-radius: $b360-border-radius-medium;
	}
}

.edit-icon {
	@include rem('padding', 0 10px);
	color: $brand-blue;
	cursor: pointer;
}

.edit-url-wrapper {
	/deep/ .input-icon {
		font-size: 1rem;
	}
}

.toggle-switch-wrapper {
	@include flex-center;
	justify-content: space-between;
	@include rem(padding-bottom, 12px);
}

.label-wrapper {
	line-height: 1.5;
	.b360-form-label {
		margin-bottom: 0;
	}
}

.form-label-subheading {
	font-weight: normal;
	color: $text-base-color;
	&.form-label-subheading__disabled {
		color: lighten($text-base-color, 20%);
	}
}

fieldset {
	display: flex;
	flex-direction: column;
	@include rem(margin, 0 0 20px 0);
	padding: 0;
	border: none;

	&:last-child {
		margin-bottom: 0;
	}

	legend {
		@include rem(margin-bottom, 12px);
		@include typo--roboto__medium;
		color: $subheader-color;

		.sub-legend {
			@include rem(padding-top, 6px);
			font-weight: normal;
			color: $text-base-color;
		}
	}
}

.error {
	@include rem(padding-bottom, 5px);
	color: $b360-color--red__error;
}

@include xl {
	.edit-url-wrapper {
		align-items: center;
		display: flex;
		/deep/ .form-inputs {
			width: 100%;
		}
	}
}
</style>
